import { type WaitForUrlMetadataResult, waitForUrlMetadata } from '@/domains/link-metadata/actions';
import { unwrapSafeActionResult } from '@/lib/actions/utils';
import { isVitest } from '@/lib/test-env';
import { isTweetUrl } from '@/lib/tweet-utils';
import type { EmbeddableStatus } from '@/lib/url-metadata/embeddability';
import { getUrlMetadataClient } from '@/lib/url-metadata/trpc-client';
import {
  HTTP_PROTOCOL_REPLACE_PATTERN,
  TRAILING_SLASH_PATTERN,
  URL_SCHEME_PREFIX_PATTERN,
  WWW_PREFIX_PATTERN,
} from '@/lib/utils/regex';
import { isPlaceholderMetadata } from './metadata';
import { FetchUrlMetadataResultSchema, parseLinkMetadata } from './schemas';
import type { LinkMetadata } from './types';

type FetchUrlMetadataMutation = {
  mutate: (input: { strategy: 'cache' | 'live'; urls: string[] }) => Promise<unknown>;
};

type UtilitiesSegment = {
  fetchUrlMetadata: FetchUrlMetadataMutation;
};

const ABSOLUTE_URL_PATTERN = URL_SCHEME_PREFIX_PATTERN;
const PLACEHOLDER_URL_BASE = 'https://placeholder.invalid';
const stripWwwPrefix = (value: string): string => value.replace(WWW_PREFIX_PATTERN, '');

class LiveWaitBatcher {
  private readonly queue = new Set<string>();
  private readonly resolvers = new Map<
    string,
    {
      resolve: (value: LinkMetadata | PromiseLike<LinkMetadata | null> | null) => void;
      reject: (error: unknown) => void;
    }
  >();
  private timer: ReturnType<typeof setTimeout> | null = null;

  enqueue(url: string): Promise<LinkMetadata | null> {
    return new Promise((resolve, reject) => {
      this.queue.add(url);
      this.resolvers.set(url, { resolve, reject });

      if (!this.timer) {
        const delay = isVitest() ? 0 : 15;
        this.timer = setTimeout(() => {
          this.flush().catch((error: unknown) => {
            for (const resolver of this.resolvers.values()) {
              resolver.reject(error);
            }
          });
        }, delay);
      }
    });
  }

  private async flush() {
    const urls = [...this.queue];
    const resolvers = new Map(this.resolvers);

    this.queue.clear();
    this.resolvers.clear();
    this.timer = null;

    if (urls.length === 0) {
      return;
    }

    try {
      // First attempt: bounded wait action (batch).
      const waitResult = await waitForUrlMetadata({ urls, timeoutMs: 8000 });
      const waitPayload = unwrapSafeActionResult<WaitForUrlMetadataResult>(waitResult);
      const pendingList = Array.isArray(waitPayload.pending) ? waitPayload.pending : [];
      const remaining = new Set(pendingList);

      for (const url of urls) {
        const resolver = resolvers.get(url);
        if (!resolver) continue;

        const meta = waitPayload.metadata[url] ?? Object.values(waitPayload.metadata)[0];
        if (meta) {
          resolver.resolve(meta);
          remaining.delete(url);
        }
      }

      if (remaining.size === 0) {
        // Resolve pending-with-null if still nothing but no pending reported.
        for (const [url, resolver] of resolvers.entries()) {
          if (!waitPayload.metadata[url]) {
            resolver.resolve(null);
          }
        }
        return;
      }

      // Fallback: batch live fetch via tRPC for the truly missing URLs.
      const fallback = await this.fetchLiveBatch([...remaining]);
      for (const [url, resolver] of resolvers.entries()) {
        if (fallback.has(url)) {
          resolver.resolve(fallback.get(url) ?? null);
        } else {
          resolver.resolve(null);
        }
      }
    } catch (error) {
      for (const resolver of resolvers.values()) {
        resolver.reject(error);
      }
    }
  }

  private async fetchLiveBatch(urls: string[]): Promise<Map<string, LinkMetadata | null>> {
    const result = new Map<string, LinkMetadata | null>();
    if (urls.length === 0) {
      return result;
    }

    try {
      const trpcClient = getUrlMetadataClient();
      const fetchUrlMetadata = getFetchUrlMetadataMutation(trpcClient);
      const responseRaw = await fetchUrlMetadata.mutate({
        strategy: 'live',
        urls,
      });
      const response = FetchUrlMetadataResultSchema.parse(responseRaw ?? {});
      const metadataEntries = Object.entries(response.metadata ?? {}).flatMap(([entryUrl, value]) => {
        const parsed = parseLinkMetadata(value);
        return parsed ? ([[entryUrl, parsed]] as const) : [];
      });
      for (const [entryUrl, meta] of metadataEntries) {
        result.set(entryUrl, meta);
      }
      for (const url of urls) {
        if (!result.has(url)) {
          result.set(url, null);
        }
      }
    } catch (error) {
      for (const url of urls) {
        result.set(url, null);
      }
      throw error;
    }

    return result;
  }
}

const liveWaitBatcher = new LiveWaitBatcher();

const isAbsoluteUrl = (value: string): boolean => ABSOLUTE_URL_PATTERN.test(value.trim());

const parseAbsoluteUrl = (value: string): URL => {
  if (!isAbsoluteUrl(value)) {
    throw new Error('URL must be absolute');
  }
  return new URL(value, PLACEHOLDER_URL_BASE);
};

export function deriveDomain(rawUrl: string): string {
  try {
    const parsed = parseAbsoluteUrl(rawUrl);
    return stripWwwPrefix(parsed.hostname);
  } catch {
    return rawUrl;
  }
}

export function computeFavicon(url: string): string | undefined {
  try {
    const domain = stripWwwPrefix(parseAbsoluteUrl(url).hostname);
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return;
  }
}

export function isTwitterImageUrl(u: string): boolean {
  try {
    const host = stripWwwPrefix(parseAbsoluteUrl(u).hostname).toLowerCase();
    return (
      host.includes('pic.twitter.com') ||
      host.includes('pic.x.com') ||
      host.includes('pbs.twimg.com') ||
      host.includes('video.twimg.com') ||
      host.includes('ton.twimg.com') ||
      host.includes('abs.twimg.com')
    );
  } catch {
    return u.includes('pic.twitter.com') || u.includes('pic.x.com');
  }
}

export function canEmbedHost(rawUrl: string): boolean {
  const skip = [
    'twitter.com',
    'x.com',
    't.co',
    'github.com',
    'gist.github.com',
    'instagram.com',
    'facebook.com',
    'fb.com',
    'linkedin.com',
    'reddit.com',
    'tiktok.com',
    'youtube.com',
    'youtu.be',
    'discord.com',
    'slack.com',
    'notion.so',
    'figma.com',
  ];
  try {
    const hostname = parseAbsoluteUrl(rawUrl).hostname.toLowerCase();
    return !skip.some((domain) => hostname.includes(domain));
  } catch {
    return false;
  }
}

export function resolvesToTweetUrl(metadata: LinkMetadata | null | undefined): boolean {
  try {
    const targetUrl = typeof metadata?.url === 'string' ? metadata.url : null;
    if (!targetUrl) return false;
    if (isTweetUrl(targetUrl)) return true;
    const parsed = parseAbsoluteUrl(targetUrl);
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname.toLowerCase();
    return (
      (host.includes('x.com') || host.includes('twitter.com')) &&
      (path.startsWith('/i/web/status/') || path.startsWith('/i/cards/'))
    );
  } catch {
    return false;
  }
}

interface ShouldAttemptLiveFetchArguments {
  metadata: LinkMetadata | null | undefined;
  placeholder: boolean;
  url: string;
}

export function determineShouldAttemptLiveFetch({
  url,
  metadata,
  placeholder,
}: ShouldAttemptLiveFetchArguments): boolean {
  try {
    const parsed = parseAbsoluteUrl(url);
    const host = parsed.hostname.toLowerCase();
    const isTco = host === 't.co' || host.endsWith('.t.co');

    if (isTco) {
      const title = typeof metadata?.title === 'string' ? metadata.title : null;
      const description = typeof metadata?.description === 'string' ? metadata.description : null;
      const image = typeof metadata?.image === 'string' ? metadata.image : null;
      const missingCore = !(metadata && (title || description || image));
      const metadataUrl = typeof metadata?.url === 'string' ? metadata.url : null;
      const unresolved = !metadata || (metadataUrl ? metadataUrl.includes('t.co') : false);
      return missingCore || unresolved;
    }

    return placeholder;
  } catch {
    return false;
  }
}

export async function fetchLiveMetadata(url: string): Promise<LinkMetadata | null> {
  try {
    return await liveWaitBatcher.enqueue(url);
  } catch {
    // Fallback to the legacy live fetch to preserve behavior if the action path fails.
    try {
      const trpcClient = getUrlMetadataClient();
      const fetchUrlMetadata = getFetchUrlMetadataMutation(trpcClient);
      const responseRaw = await fetchUrlMetadata.mutate({
        strategy: 'live',
        urls: [url],
      });
      const response = FetchUrlMetadataResultSchema.parse(responseRaw ?? {});
      const metadataEntries = Object.entries(response.metadata ?? {}).flatMap(([entryUrl, value]) => {
        const parsed = parseLinkMetadata(value);
        return parsed ? ([[entryUrl, parsed]] as const) : [];
      });
      const metadataMap = new Map<string, LinkMetadata>(metadataEntries);
      if (metadataMap.has(url)) {
        return metadataMap.get(url) ?? null;
      }
      const firstEntry = metadataEntries[0]?.[1];
      return firstEntry ?? null;
    } catch {
      return null;
    }
  }
}

export function getSummarizerTitle(url: string, isTweet: boolean): string {
  const trimmedUrl = url.trim();
  if (isTweet) {
    return 'Summarize Twitter conversation';
  }

  let host: string | null = null;
  let loweredUrl = trimmedUrl.toLowerCase();
  if (trimmedUrl) {
    try {
      const parsed = parseAbsoluteUrl(trimmedUrl);
      host = stripWwwPrefix(parsed.hostname).toLowerCase();
      loweredUrl = parsed.toString().toLowerCase();
    } catch {
      host = null;
    }
  }

  const isYouTubeHost =
    (host ? host.includes('youtube.com') || host.includes('youtu.be') : false) ||
    loweredUrl.includes('youtube.com') ||
    loweredUrl.includes('youtu.be');
  if (isYouTubeHost) {
    return 'Summarize YouTube video';
  }

  const defaultLabel = trimmedUrl
    .replace(HTTP_PROTOCOL_REPLACE_PATTERN, '')
    .replace(WWW_PREFIX_PATTERN, '')
    .replace(TRAILING_SLASH_PATTERN, '')
    .trim();

  const label = defaultLabel || 'link';
  return `Summarize ${label}`;
}

export function formatDisplayUrl(url: string): string {
  try {
    const parsed = parseAbsoluteUrl(url);
    const host = stripWwwPrefix(parsed.hostname);
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    return `${host}${path}`;
  } catch {
    return url
      .replace(HTTP_PROTOCOL_REPLACE_PATTERN, '')
      .replace(WWW_PREFIX_PATTERN, '')
      .replace(TRAILING_SLASH_PATTERN, '');
  }
}

export function shouldPrefetchIframe(url: string, embeddableStatus: EmbeddableStatus, isExcluded: boolean): boolean {
  if (!url || isExcluded) {
    return false;
  }

  if (embeddableStatus === 'deny') {
    return false;
  }

  if (embeddableStatus === 'allow') {
    return true;
  }

  return !isVitest();
}

export function resolvePlaceholderFlag(url: string, metadata: LinkMetadata | null | undefined): boolean {
  return isPlaceholderMetadata(metadata, url);
}

const getFetchUrlMetadataMutation = (client: ReturnType<typeof getUrlMetadataClient>): FetchUrlMetadataMutation => {
  const utilities = client.utilities as UtilitiesSegment | undefined;
  if (!utilities?.fetchUrlMetadata) {
    throw new Error('tRPC utilities.fetchUrlMetadata mutation unavailable');
  }
  return utilities.fetchUrlMetadata;
};
